import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrms } from '../../services/api';
import Button from '../../components/common/Button';
import { theme } from '../../theme';

const inp = {
  width: '100%', padding: '0.5rem 0.75rem',
  border: '1px solid #374151', borderRadius: 0,
  fontSize: '0.875rem', color: '#111', background: '#fff',
  outline: 'none', boxSizing: 'border-box',
};

const lbl = {
  fontSize: '0.775rem', fontWeight: 600, color: '#374151',
  display: 'block', marginBottom: '0.25rem',
};

const tableRow = {
  display: 'grid',
  gridTemplateColumns: '2fr 1.5fr 1fr',
  borderBottom: '1px solid #d1d5db',
};

const th = {
  padding: '0.45rem 0.75rem',
  borderRight: '1px solid #d1d5db',
  fontSize: '0.775rem', fontWeight: 700,
  background: '#f3f4f6', color: '#374151',
};

const td = {
  padding: '0.35rem 0.5rem',
  borderRight: '1px solid #d1d5db',
};

const sectionHead = {
  background: '#0f4c75', color: '#fff',
  fontSize: '0.8rem', fontWeight: 700,
  padding: '0.5rem 0.75rem', letterSpacing: '0.04em',
  borderBottom: '1px solid #d1d5db',
};

const radio = { display: 'flex', gap: '1.5rem', alignItems: 'center' };

const EMPTY_MEMBER = { name: '', relation: '', dob: '', is_residing_away: false };

export default function ESIForm1() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    insurance_number: '',
    full_name: '', father_husband_name: '',
    dob: '', gender: 'Male', marital_status: 'Unmarried',
    permanent_address: '', local_address: '',
    date_of_appointment: '',
    nominee_name: '', nominee_relation: '', nominee_address: '', nominee_dob: '',
  });
  const [familyMembers, setFamilyMembers] = useState([{ ...EMPTY_MEMBER }]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadExisting(); }, []);

  const loadExisting = async () => {
    try {
      const r = await hrms.getMyESIDeclaration();
      if (r.data) {
        const d = r.data;
        setSubmitted(d.is_submitted);
        setForm({
          insurance_number: d.insurance_number || '',
          full_name: d.full_name || '',
          father_husband_name: d.father_husband_name || '',
          dob: d.dob || '',
          gender: d.gender || 'Male',
          marital_status: d.marital_status || 'Unmarried',
          permanent_address: d.permanent_address || '',
          local_address: d.local_address || '',
          date_of_appointment: d.date_of_appointment || '',
          nominee_name: d.nominee_name || '',
          nominee_relation: d.nominee_relation || '',
          nominee_address: d.nominee_address || '',
          nominee_dob: d.nominee_dob || '',
        });
        if (d.family_members) {
          try {
            const parsed = JSON.parse(d.family_members);
            if (Array.isArray(parsed) && parsed.length > 0) setFamilyMembers(parsed);
          } catch {}
        }
      }
    } catch {}
  };

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const updateMember = (idx, field, value) =>
    setFamilyMembers(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));

  const handleSave = async (submit = false) => {
    setLoading(true); setError('');
    try {
      const filledMembers = familyMembers.filter(m => m.name.trim());
      await hrms.saveESIDeclaration({
        ...form,
        dob: form.dob || undefined,
        date_of_appointment: form.date_of_appointment || undefined,
        nominee_dob: form.nominee_dob || undefined,
        family_members: filledMembers.length > 0 ? JSON.stringify(filledMembers) : undefined,
        is_submitted: submit,
      });
      if (submit) setSubmitted(true);
      navigate('/employee/dashboard');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const readOnly = submitted;

  return (
    <div style={{ maxWidth: '900px', fontFamily: 'Arial, sans-serif' }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <Button variant="outlineGray" onClick={() => navigate('/employee/complete-profile')}>← Back</Button>
        {submitted && (
          <span style={{ background: '#DCFCE7', color: '#166534', borderRadius: 20, padding: '0.25rem 0.875rem', fontSize: '0.8rem', fontWeight: 600 }}>
            ✓ Submitted
          </span>
        )}
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', color: theme.danger, fontSize: '0.875rem' }}>
          ⚠️ {error}
        </div>
      )}

      {/* ── Government Form Template ── */}
      <div style={{ border: '2px solid #0f4c75', background: '#fff' }}>

        {/* Form Header */}
        <div style={{ background: '#0f4c75', padding: '0.875rem 1rem', textAlign: 'center' }}>
          <p style={{ color: '#fbbf24', fontSize: '0.75rem', margin: 0, letterSpacing: '0.1em' }}>EMPLOYEES' STATE INSURANCE CORPORATION</p>
          <h2 style={{ color: '#fff', margin: '0.25rem 0 0', fontSize: '1rem', fontWeight: 800 }}>FORM 1</h2>
          <p style={{ color: '#93c5fd', fontSize: '0.7rem', margin: '0.25rem 0 0' }}>
            [See Regulation 11 and 12 of the E.S.I. (General) Regulations, 1950]
          </p>
          <p style={{ color: '#fcd34d', fontSize: '0.75rem', margin: '0.375rem 0 0', fontWeight: 600 }}>DECLARATION FORM</p>
        </div>

        {/* Establishment info strip */}
        <div style={{ background: '#f0f8ff', borderBottom: '1px solid #d1d5db', padding: '0.5rem 0.75rem', display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.78rem' }}>
          <span><strong>Name of Employer:</strong> BonTon Softwares Pvt Ltd</span>
          <span><strong>Code No. of Employer:</strong> (to be filled by HR)</span>
        </div>

        {/* PART A */}
        <div style={sectionHead}>PART A — PARTICULARS OF THE INSURED PERSON</div>
        <div style={{ padding: '0.75rem', borderBottom: '1px solid #d1d5db' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px,1fr))', gap: '0.875rem', marginBottom: '0.75rem' }}>
            <div>
              <span style={lbl}>1. Insurance Number (if previously insured, leave blank if new)</span>
              <input style={inp} name="insurance_number" value={form.insurance_number} onChange={handleChange} readOnly={readOnly} placeholder="Leave blank if new employee" />
            </div>
            <div>
              <span style={lbl}>2. Name in Full (in Block Letters)</span>
              <input style={inp} name="full_name" value={form.full_name} onChange={handleChange} readOnly={readOnly} placeholder="FULL NAME" />
            </div>
            <div>
              <span style={lbl}>3. Father's / Husband's Name</span>
              <input style={inp} name="father_husband_name" value={form.father_husband_name} onChange={handleChange} readOnly={readOnly} />
            </div>
            <div>
              <span style={lbl}>4. Date of Birth</span>
              <input style={inp} type="date" name="dob" value={form.dob} onChange={handleChange} readOnly={readOnly} />
            </div>
            <div>
              <span style={lbl}>5. Sex</span>
              <div style={radio}>
                {['Male', 'Female', 'Other'].map(v => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="radio" name="gender" value={v} checked={form.gender === v} onChange={handleChange} disabled={readOnly} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <span style={lbl}>Marital Status</span>
              <div style={radio}>
                {['Unmarried', 'Married', 'Widow/Widower'].map(v => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="radio" name="marital_status" value={v} checked={form.marital_status === v} onChange={handleChange} disabled={readOnly} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <span style={lbl}>8. Date of Appointment</span>
              <input style={inp} type="date" name="date_of_appointment" value={form.date_of_appointment} onChange={handleChange} readOnly={readOnly} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
            <div>
              <span style={lbl}>6. Permanent Home Address</span>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} name="permanent_address" value={form.permanent_address} onChange={handleChange} readOnly={readOnly} placeholder="Door No, Street, City, State, PIN" />
            </div>
            <div>
              <span style={lbl}>7. Present / Local Address (if different from permanent)</span>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} name="local_address" value={form.local_address} onChange={handleChange} readOnly={readOnly} placeholder="Leave blank if same as permanent address" />
            </div>
          </div>
        </div>

        {/* PART B — Family Members */}
        <div style={sectionHead}>PART B — FAMILY MEMBERS FOR MEDICAL BENEFIT</div>
        <div style={{ borderBottom: '1px solid #d1d5db' }}>
          <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#555', borderBottom: '1px solid #e5e7eb' }}>
            List all dependant family members who reside with you. Mark * against members permanently residing away from your place of employment.
          </div>
          {/* Table header */}
          <div style={tableRow}>
            <div style={th}>Name</div>
            <div style={th}>Relationship</div>
            <div style={{ ...th, borderRight: 'none' }}>Date of Birth</div>
          </div>
          {familyMembers.map((member, idx) => (
            <div key={idx} style={{ ...tableRow, alignItems: 'center' }}>
              <div style={td}>
                <input style={{ ...inp, border: 'none', padding: '0.25rem' }} value={member.name}
                  onChange={e => updateMember(idx, 'name', e.target.value)} readOnly={readOnly} placeholder={`Member ${idx + 1} name`} />
              </div>
              <div style={td}>
                <input style={{ ...inp, border: 'none', padding: '0.25rem' }} value={member.relation}
                  onChange={e => updateMember(idx, 'relation', e.target.value)} readOnly={readOnly} placeholder="e.g. Spouse, Son" />
              </div>
              <div style={{ ...td, borderRight: 'none', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input style={{ ...inp, border: 'none', padding: '0.25rem' }} type="date" value={member.dob}
                  onChange={e => updateMember(idx, 'dob', e.target.value)} readOnly={readOnly} />
                {!readOnly && familyMembers.length > 1 && (
                  <button type="button" onClick={() => setFamilyMembers(p => p.filter((_, i) => i !== idx))}
                    style={{ background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>✕</button>
                )}
              </div>
            </div>
          ))}
          {!readOnly && familyMembers.length < 8 && (
            <div style={{ padding: '0.5rem 0.75rem' }}>
              <button type="button" onClick={() => setFamilyMembers(p => [...p, { ...EMPTY_MEMBER }])}
                style={{ background: 'none', border: `1.5px dashed #9ca3af`, borderRadius: 4, padding: '0.4rem 1rem', color: '#374151', cursor: 'pointer', fontSize: '0.8rem' }}>
                + Add Family Member
              </button>
            </div>
          )}
        </div>

        {/* PART C — Nomination */}
        <div style={sectionHead}>PART C — NOMINATION FOR CASH BENEFITS</div>
        <div style={{ borderBottom: '1px solid #d1d5db', padding: '0.75rem' }}>
          <p style={{ fontSize: '0.75rem', color: '#555', marginBottom: '0.75rem', marginTop: 0 }}>
            In the event of my death, I hereby nominate the following person(s) to receive the cash benefits payable under the ESI Act:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: '0.75rem' }}>
            <div>
              <span style={lbl}>Name of Nominee</span>
              <input style={inp} name="nominee_name" value={form.nominee_name} onChange={handleChange} readOnly={readOnly} />
            </div>
            <div>
              <span style={lbl}>Relationship to Insured Person</span>
              <input style={inp} name="nominee_relation" value={form.nominee_relation} onChange={handleChange} readOnly={readOnly} placeholder="e.g. Spouse, Son" />
            </div>
            <div>
              <span style={lbl}>Date of Birth of Nominee</span>
              <input style={inp} type="date" name="nominee_dob" value={form.nominee_dob} onChange={handleChange} readOnly={readOnly} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <span style={lbl}>Address of Nominee</span>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} name="nominee_address" value={form.nominee_address} onChange={handleChange} readOnly={readOnly} placeholder="Full address of nominee" />
            </div>
          </div>
        </div>

        {/* Declaration */}
        <div style={{ padding: '0.875rem 0.75rem', borderBottom: '1px solid #d1d5db', background: '#fafafa', fontSize: '0.78rem', color: '#374151', lineHeight: 1.6 }}>
          <strong>DECLARATION:</strong> I hereby declare that the particulars given above are correct and that I shall promptly inform the employer of any subsequent changes therein.
          I also declare that the person(s) entered in Part B above are wholly dependent upon me for their maintenance.
        </div>

        {/* Signature block */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '1rem 0.75rem', gap: '2rem' }}>
          <div>
            <p style={{ fontSize: '0.78rem', color: '#374151', margin: 0 }}>Date: _______________</p>
            <p style={{ fontSize: '0.78rem', color: '#374151', marginTop: '0.5rem' }}>Place: _______________</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ borderBottom: '1px solid #374151', width: 200, marginLeft: 'auto', marginBottom: '0.25rem', height: 40 }} />
            <p style={{ fontSize: '0.78rem', color: '#374151', margin: 0 }}>Signature / Thumb Impression of Employee</p>
          </div>
        </div>

        {/* Employer verification strip */}
        <div style={{ borderTop: '2px solid #0f4c75', background: '#f0f8ff', padding: '0.625rem 0.75rem', fontSize: '0.75rem', color: '#374151' }}>
          <strong>For Employer Use:</strong>&nbsp; Verified and forwarded.&nbsp;&nbsp;
          Employer Signature: _______________ &nbsp;&nbsp; Date: _______________ &nbsp;&nbsp; Stamp: _______________
        </div>
      </div>

      {/* Action buttons */}
      {!readOnly && (
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingTop: '1.25rem', paddingBottom: '2rem' }}>
          <Button variant="outlineGray" onClick={() => handleSave(false)} loading={loading}>Save Draft</Button>
          <Button variant="primary" onClick={() => handleSave(true)} loading={loading}>Submit Declaration</Button>
        </div>
      )}
      {readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1.25rem', paddingBottom: '2rem' }}>
          <Button variant="outlineGray" onClick={() => navigate('/employee/dashboard')}>Back to Dashboard</Button>
        </div>
      )}
    </div>
  );
}
